import React, { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@mui/material'
import { Description, Event, HomeWork } from '@mui/icons-material'

import { FlexDiv } from '../FlexDiv'
import {
    TenantLeaseOffer,
    TransferOptions,
    TransferRequestPayload,
} from '../../hooks/useLeaseOffers'

interface LeaseOfferCardProps {
    offer: TenantLeaseOffer
    acting?: boolean
    onAccept: (id: number) => Promise<void>
    onDecline: (id: number, reason: string) => Promise<void>
    onLoadTransferOptions?: (id: number) => Promise<TransferOptions>
    onTransfer?: (id: number, payload: TransferRequestPayload) => Promise<void>
}

const formatMoney = (value: string) => {
    const amount = Number(value)
    if (Number.isNaN(amount)) return value
    return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })
}

const formatDate = (value?: string | null): string | null => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })
}

export const LeaseOfferCard: React.FC<LeaseOfferCardProps> = ({
    offer,
    acting = false,
    onAccept,
    onDecline,
    onLoadTransferOptions,
    onTransfer,
}) => {
    const theme = useTheme()
    const [confirmAccept, setConfirmAccept] = useState(false)
    const [confirmDecline, setConfirmDecline] = useState(false)
    const [confirmTransfer, setConfirmTransfer] = useState(false)
    const [declineReason, setDeclineReason] = useState('')
    const [transferOptions, setTransferOptions] =
        useState<TransferOptions | null>(null)
    const [desiredUnitConfig, setDesiredUnitConfig] = useState<number | ''>('')
    const [roommateIds, setRoommateIds] = useState<number[]>([])
    const [transferNotes, setTransferNotes] = useState('')
    const [loadingTransfer, setLoadingTransfer] = useState(false)
    const isNew = offer.kind === 'new'
    const title = isNew ? 'Your Lease Offer' : 'Your Renewal Offer'
    const floorPlanName = offer.unit_config_name || offer.area_config_name
    const sizeParts = [
        offer.bed_count != null ? `${offer.bed_count} BD` : null,
        offer.bath_count != null ? `${offer.bath_count} BA` : null,
    ].filter(Boolean)
    const sizeLabel = sizeParts.length ? sizeParts.join(' / ') : null
    const unitNumber = offer.unit_name ? `Unit ${offer.unit_name}` : null
    const unitLabel =
        [floorPlanName, sizeLabel, unitNumber].filter(Boolean).join(' · ') ||
        null
    const deadline = formatDate(offer.expires_at)
    const moveOutLabel = formatDate(offer.proposed_end_date) ?? '—'
    const canTransfer =
        offer.can_transfer && onLoadTransferOptions != null && onTransfer != null

    const handleAccept = async () => {
        try {
            await onAccept(offer.id)
            setConfirmAccept(false)
        } catch {
            // Toast is handled by the hook.
        }
    }

    const handleDecline = async () => {
        try {
            await onDecline(offer.id, declineReason)
            setConfirmDecline(false)
            setDeclineReason('')
        } catch {
            // Toast is handled by the hook.
        }
    }

    const openTransfer = async () => {
        if (!onLoadTransferOptions) return
        setLoadingTransfer(true)
        try {
            const options = await onLoadTransferOptions(offer.id)
            setTransferOptions(options)
            setDesiredUnitConfig('')
            setRoommateIds([])
            setTransferNotes('')
            setConfirmTransfer(true)
        } catch {
            // Toast is handled by the hook if the request fails later.
        } finally {
            setLoadingTransfer(false)
        }
    }

    const handleTransfer = async () => {
        if (!onTransfer) return
        try {
            await onTransfer(offer.id, {
                desired_unit_config:
                    desiredUnitConfig === '' ? null : Number(desiredUnitConfig),
                roommate_ids: roommateIds,
                notes: transferNotes,
            })
            setConfirmTransfer(false)
        } catch {
            // Toast is handled by the hook.
        }
    }

    const toggleRoommate = (userId: number) => {
        setRoommateIds((current) =>
            current.includes(userId)
                ? current.filter((id) => id !== userId)
                : [...current, userId],
        )
    }

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 3,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />

            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                    flexWrap: 'wrap',
                    gap: 8,
                }}
            >
                <FlexDiv style={{ alignItems: 'center' }}>
                    <HomeWork
                        style={{
                            color: theme.palette.success.main,
                            marginRight: theme.spacing(1),
                            fontSize: 24,
                        }}
                    />
                    <Typography
                        variant="h6"
                        style={{ color: '#fff', fontWeight: 600 }}
                    >
                        {title}
                    </Typography>
                </FlexDiv>
                {deadline && (
                    <Chip
                        icon={<Event />}
                        label={`Respond by ${deadline}`}
                        size="small"
                        color="warning"
                        sx={{
                            fontWeight: 600,
                            color: '#3e2723',
                            '& .MuiChip-label': { color: '#3e2723' },
                            '& .MuiChip-icon': { color: '#3e2723' },
                        }}
                    />
                )}
            </FlexDiv>

            {unitLabel && (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                        Floor plan
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: '#fff', fontWeight: 600 }}
                    >
                        {unitLabel}
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Move-in date
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                        {formatDate(offer.proposed_start_date) ?? '—'}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Move-out date
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                        {moveOutLabel}
                    </Typography>
                </Box>
            </Box>

            {(offer.charge_lines?.length ?? 0) > 0 ? (
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                        Charges
                    </Typography>
                    {offer.charge_lines?.map((line) => (
                        <Box
                            key={line.id}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#fff' }}>
                                {line.label}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>
                                {formatMoney(line.amount)}
                            </Typography>
                        </Box>
                    ))}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 0.5,
                            pt: 0.5,
                            borderTop: '1px solid rgba(255,255,255,0.16)',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: '#fff', fontWeight: 600 }}
                        >
                            Monthly total
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: '#fff', fontWeight: 600 }}
                        >
                            {formatMoney(offer.proposed_rent)}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Monthly rent
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                        {formatMoney(offer.proposed_rent)}
                    </Typography>
                </Box>
            )}

            {offer.concession_name && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        backgroundColor: 'rgba(70, 176, 131, 0.16)',
                        color: '#fff',
                        '& .MuiAlert-icon': { color: '#46b083' },
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                        {offer.concession_name}
                    </Typography>
                    {offer.concession_description && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            {offer.concession_description}
                        </Typography>
                    )}
                </Alert>
            )}

            <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mb: 2 }}
            >
                This PDF is an offer of terms, not the lease. After you accept,
                you will still sign the lease packet.
            </Typography>

            <FlexDiv style={{ gap: 8, flexWrap: 'wrap' }}>
                {offer.pdf_url && (
                    <Button
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={() =>
                            window.open(offer.pdf_url!, '_blank', 'noopener,noreferrer')
                        }
                        sx={{
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.55)',
                            '&:hover': {
                                borderColor: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.08)',
                            },
                        }}
                    >
                        View offer PDF
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    disabled={acting}
                    onClick={() => setConfirmAccept(true)}
                >
                    Accept
                </Button>
                <Button
                    variant="outlined"
                    disabled={acting}
                    onClick={() => setConfirmDecline(true)}
                    sx={{
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.55)',
                        '&:hover': {
                            borderColor: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.08)',
                        },
                    }}
                >
                    Decline
                </Button>
                {canTransfer && (
                    <Button
                        variant="outlined"
                        disabled={acting || loadingTransfer}
                        onClick={openTransfer}
                        sx={{
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.55)',
                            '&:hover': {
                                borderColor: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.08)',
                            },
                        }}
                    >
                        {loadingTransfer ? 'Loading…' : 'Request transfer'}
                    </Button>
                )}
            </FlexDiv>

            <Dialog open={confirmAccept} onClose={() => setConfirmAccept(false)}>
                <DialogTitle>
                    {isNew ? 'Accept this lease offer?' : 'Accept this renewal?'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {isNew
                            ? 'A pending lease will be created with these terms. You will still need to sign the lease packet.'
                            : 'A renewal request will be submitted to your property manager.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAccept(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={acting}
                        onClick={handleAccept}
                    >
                        Accept offer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDecline} onClose={() => setConfirmDecline(false)}>
                <DialogTitle>Decline this offer?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        You can optionally share why you are declining.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason (optional)"
                        value={declineReason}
                        onChange={(event) => setDeclineReason(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDecline(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={acting}
                        onClick={handleDecline}
                    >
                        Decline offer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmTransfer}
                onClose={() => setConfirmTransfer(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Request a transfer?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Tell the property team where you would like to move.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="transfer-unit-type-label">
                            What unit type are you interested in?
                        </InputLabel>
                        <Select
                            labelId="transfer-unit-type-label"
                            label="What unit type are you interested in?"
                            value={desiredUnitConfig}
                            onChange={(event) =>
                                setDesiredUnitConfig(
                                    event.target.value === ''
                                        ? ''
                                        : Number(event.target.value),
                                )
                            }
                        >
                            <MenuItem value="">No preference</MenuItem>
                            {(transferOptions?.unit_configs ?? []).map((config) => (
                                <MenuItem key={config.id} value={config.id}>
                                    {config.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(transferOptions?.roommates ?? []).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Who will be transferring with you?
                            </Typography>
                            <FormGroup>
                                {transferOptions!.roommates.map((roommate) => (
                                    <FormControlLabel
                                        key={roommate.user_id}
                                        control={
                                            <Checkbox
                                                checked={roommateIds.includes(
                                                    roommate.user_id,
                                                )}
                                                onChange={() =>
                                                    toggleRoommate(roommate.user_id)
                                                }
                                            />
                                        }
                                        label={roommate.name}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Any additional info for the property team?"
                        value={transferNotes}
                        onChange={(event) => setTransferNotes(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmTransfer(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={acting}
                        onClick={handleTransfer}
                    >
                        Submit transfer request
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
